import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';

interface Props {
  isDark: boolean;
  onToggle: () => void;
}

const SunIcon = () => (
  <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round'>
    <circle cx='12' cy='12' r='5' />
    <line x1='12' y1='2' x2='12' y2='4' />
    <line x1='12' y1='20' x2='12' y2='22' />
    <line x1='4.22' y1='4.22' x2='5.64' y2='5.64' />
    <line x1='18.36' y1='18.36' x2='19.78' y2='19.78' />
    <line x1='2' y1='12' x2='4' y2='12' />
    <line x1='20' y1='12' x2='22' y2='12' />
    <line x1='4.22' y1='19.78' x2='5.64' y2='18.36' />
    <line x1='18.36' y1='5.64' x2='19.78' y2='4.22' />
  </svg>
);

const MoonIcon = () => (
  <svg width='9' height='9' viewBox='0 0 24 24' fill='currentColor'>
    <path d='M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' />
  </svg>
);

const TRAVEL = 20; // px the knob slides

const DarkModeToggle: React.FC<Props> = ({ isDark, onToggle }) => {
  const knobRef = useRef<HTMLSpanElement>(null);
  const sunRef = useRef<HTMLSpanElement>(null);
  const moonRef = useRef<HTMLSpanElement>(null);
  const initialized = useRef(false);

  // Set initial GSAP state without animating on first render
  useEffect(() => {
    if (!knobRef.current || !sunRef.current || !moonRef.current) return;

    if (!initialized.current) {
      initialized.current = true;
      gsap.set(knobRef.current, { x: isDark ? TRAVEL : 0 });
      gsap.set(sunRef.current, {
        autoAlpha: isDark ? 0 : 1,
        rotate: isDark ? 90 : 0,
        scale: isDark ? 0.4 : 1,
      });
      gsap.set(moonRef.current, {
        autoAlpha: isDark ? 1 : 0,
        rotate: isDark ? 0 : -90,
        scale: isDark ? 1 : 0.4,
      });
      return;
    }

    // Animated transition
    const tl = gsap.timeline();
    if (isDark) {
      tl.to(knobRef.current, { x: TRAVEL, duration: 0.42, ease: 'back.out(2)' }, 0)
        .to(sunRef.current, { autoAlpha: 0, rotate: 90, scale: 0.4, duration: 0.2, ease: 'power2.in' }, 0)
        .fromTo(
          moonRef.current,
          { autoAlpha: 0, rotate: -90, scale: 0.4 },
          { autoAlpha: 1, rotate: 0, scale: 1, duration: 0.32, ease: 'back.out(1.8)' },
          0.15
        );
    } else {
      tl.to(knobRef.current, { x: 0, duration: 0.42, ease: 'back.out(2)' }, 0)
        .to(moonRef.current, { autoAlpha: 0, rotate: 90, scale: 0.4, duration: 0.2, ease: 'power2.in' }, 0)
        .fromTo(
          sunRef.current,
          { autoAlpha: 0, rotate: -90, scale: 0.4 },
          { autoAlpha: 1, rotate: 0, scale: 1, duration: 0.32, ease: 'back.out(1.8)' },
          0.15
        );
    }
  }, [isDark]);

  return (
    <button
      onClick={onToggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={`relative inline-flex items-center w-11 h-6 rounded-full border transition-colors duration-300 focus:outline-none flex-shrink-0 ${
        isDark
          ? 'bg-accent/10 border-accent/25'
          : 'bg-surface border-line'
      }`}
    >
      <span
        ref={knobRef}
        className={`absolute left-[3px] w-[18px] h-[18px] rounded-full flex items-center justify-center will-change-transform transition-colors duration-300 ${
          isDark ? 'bg-accent text-paper' : 'bg-ink text-paper'
        }`}
      >
        <span ref={sunRef} className='absolute flex items-center justify-center'>
          <SunIcon />
        </span>
        <span ref={moonRef} className='absolute flex items-center justify-center'>
          <MoonIcon />
        </span>
      </span>
    </button>
  );
};

export default DarkModeToggle;
